<template>
  <NcrmRecordLayout :left-lg-cols="3" :main-lg-cols-both="7" :right-lg-cols="2">
    <template #header>
      <NcrmRecordHeader
        :title="recordTitle"
        :subtitle="recordSubtitle"
        :chips="headerChips"
        show-back-button
        :back-aria-label="$t('app.actions.back')"
        @back-click="goBack"
      >
        <template #below>
          <div v-if="headerProgressStages.length" class="ncrm-onboarding-details__header-progress" aria-hidden="true">
            <div
              v-for="stage in headerProgressStages"
              :key="`onboarding-header-progress-stage-${stage.id}`"
              class="ncrm-onboarding-details__header-progress-stage"
              :class="{ 'is-current-stage': stage.id === currentStageId }"
              :style="{ '--stage-weight': stage.weight }"
            >
              <span
                v-for="taskIndex in stage.tickCount"
                :key="`onboarding-header-progress-stage-${stage.id}-task-${taskIndex}`"
                class="ncrm-onboarding-details__header-progress-tick"
                :class="headerProgressTickState(stage, taskIndex - 1)"
              />
            </div>
          </div>
        </template>
        <template #actions>
          <div class="d-flex align-center flex-wrap ga-2">
            <v-btn
              size="small"
              color="primary"
              variant="tonal"
              :disabled="!hasContractLink"
              @click="openContract"
            >
              <v-icon start>mdi-file-document-outline</v-icon>
              {{ $t("ncrm.onboarding.details.actions.openContract") }}
            </v-btn>
            <v-btn
              size="small"
              color="primary"
              variant="elevated"
              :disabled="!canMoveToNextStage || stageMoveLoading"
              :loading="stageMoveLoading"
              @click="moveToNextStage"
            >
              <v-icon start>mdi-arrow-right-bold-circle-outline</v-icon>
              {{ $t("ncrm.onboarding.details.actions.moveNext") }}
            </v-btn>
          </div>
        </template>
      </NcrmRecordHeader>
    </template>

    <template #left>
      <v-card class="pa-4 h-100">
        <div class="text-subtitle-1 ncrm-text-strong mb-3">
          {{ $t("ncrm.onboarding.details.context.title") }}
        </div>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

        <div v-else class="ncrm-onboarding-details__kv-list">
          <div class="ncrm-onboarding-details__kv-item">
            <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.context.contract") }}</div>
            <div class="text-body-2 ncrm-text-strong">{{ valueOrDash(item?.contract_number || item?.contract_name) }}</div>
          </div>
          <div class="ncrm-onboarding-details__kv-item">
            <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.context.company") }}</div>
            <div class="text-body-2">{{ valueOrDash(item?.company_name) }}</div>
          </div>
          <div class="ncrm-onboarding-details__kv-item">
            <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.context.product") }}</div>
            <div class="text-body-2">{{ valueOrDash(item?.product_name_snapshot) }}</div>
          </div>
          <div class="ncrm-onboarding-details__kv-item">
            <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.context.stage") }}</div>
            <div class="text-body-2">{{ valueOrDash(item?.current_stage_name) }}</div>
          </div>
          <NcrmOnboardingOwnerField :onboarding-id="normalizedOnboardingId" :item="item"
            @updated="item = $event || item" @notify="showSnackbar($event.message, $event.color || 'info')" />
          <div class="ncrm-onboarding-details__kv-item">
            <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.context.kickoffAt") }}</div>
            <div class="text-body-2">{{ formatDate(item?.kickoff_at) }}</div>
          </div>
          <div class="ncrm-onboarding-details__kv-item">
            <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.context.goLiveTargetAt") }}</div>
            <div class="text-body-2">{{ formatDate(item?.go_live_target_at) }}</div>
          </div>
          <div class="ncrm-onboarding-details__kv-item">
            <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.context.goLiveAt") }}</div>
            <div class="text-body-2">{{ formatDate(item?.go_live_at) }}</div>
          </div>
        </div>
      </v-card>
    </template>

    <template #main>
      <v-card>
        <v-tabs v-model="activeTab" density="comfortable">
          <v-tab value="activity">{{ $t("ncrm.onboarding.details.tabs.activity") }}</v-tab>
          <v-tab value="payments">{{ $t("ncrm.onboarding.details.tabs.payments") }}</v-tab>
          <v-tab value="history">{{ $t("ncrm.onboarding.details.tabs.history") }}</v-tab>
        </v-tabs>

        <v-divider />

        <v-window v-model="activeTab" class="ncrm-onboarding-details__window" :touch="false">
          <v-window-item value="activity">
            <v-card-text class="pa-3">
              <NcrmActivityTimeline
                v-if="hasDealLink"
                entity-type="deal"
                :entity-id="item.deal_id"
                entity-context="onboarding"
                :onboarding-id="item.id"
                :title="$t('ncrm.onboarding.details.activity.title')"
                :subtitle="$t('ncrm.onboarding.details.activity.subtitle')"
                :max-items="30"
                sort-mode="active_overdue_first"
                @changed="handleActivityTimelineChanged"
              />
              <v-alert v-else type="info" density="comfortable">
                {{ $t("ncrm.onboarding.details.activity.missingDeal") }}
              </v-alert>

              <NcrmContractItemFieldsCard
                v-if="contractItemFieldRecord"
                :contract-id="item?.contract_id"
                :contract-item="contractItemFieldRecord"
                :allow-add="false"
                :show-item-summary="false"
                :current-stage-id="currentStageId"
                :visible-stage-ids="visibleFieldStageIds"
                class="mt-4"
                @updated="loadOnboarding"
                @notify="handleFieldNotification"
              />
            </v-card-text>
          </v-window-item>

          <v-window-item value="payments">
            <v-card-text>
              <div class="text-subtitle-2 ncrm-text-strong mb-3">
                {{ $t("ncrm.onboarding.details.payments.title") }}
              </div>
              <div class="ncrm-onboarding-details__kv-list">
                <div class="ncrm-onboarding-details__kv-item">
                  <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.payments.totalPayments") }}</div>
                  <div class="text-body-2 ncrm-text-strong">{{ formatNumber(paymentSummary.total_payments) }}</div>
                </div>
                <div class="ncrm-onboarding-details__kv-item">
                  <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.payments.paidAmount") }}</div>
                  <div class="text-body-2">{{ formatMoney(paymentSummary.paid_amount) }}</div>
                </div>
                <div class="ncrm-onboarding-details__kv-item">
                  <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.payments.openAmount") }}</div>
                  <div class="text-body-2">{{ formatMoney(paymentSummary.open_amount) }}</div>
                </div>
                <div class="ncrm-onboarding-details__kv-item">
                  <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.payments.overdueAmount") }}</div>
                  <div class="text-body-2">
                    <v-chip :color="Number(paymentSummary.overdue_amount || 0) > 0 ? 'error' : 'success'">
                      {{ formatMoney(paymentSummary.overdue_amount) }}
                    </v-chip>
                  </div>
                </div>
                <div class="ncrm-onboarding-details__kv-item">
                  <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.payments.nextDueAt") }}</div>
                  <div class="text-body-2">{{ formatDate(paymentSummary.next_due_at) }}</div>
                </div>
              </div>
            </v-card-text>
          </v-window-item>

          <v-window-item value="history">
            <v-card-text>
              <v-alert v-if="!historyItems.length" type="info" density="comfortable">
                {{ $t("ncrm.onboarding.details.history.empty") }}
              </v-alert>

              <v-list v-else lines="two" density="comfortable" class="pa-0">
                <v-list-item
                  v-for="entry in historyItems"
                  :key="`onboarding-history-${entry.id}`"
                  class="ncrm-onboarding-details__list-item"
                >
                  <v-list-item-title>
                    <v-chip size="x-small" color="info" class="mr-2">
                      {{ historyEventLabel(entry.event_type) }}
                    </v-chip>
                    {{ historyValueLabel(entry) }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ $t("ncrm.onboarding.details.history.changedByAt", {
                      user: valueOrDash(entry.changed_by_name || entry.changed_by_user_id),
                      date: formatDateTime(entry.created_at),
                    }) }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-window-item>
        </v-window>
      </v-card>
    </template>

    <template #right>
      <v-card class="pa-4 h-100">
        <div class="text-subtitle-1 ncrm-text-strong mb-3">
          {{ $t("ncrm.onboarding.details.tabs.tasks") }}
        </div>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

        <div v-else>
          <div class="ncrm-onboarding-details__stage-actions mb-3">
            <v-select
              v-model="stageMoveTargetId"
              :items="stageMoveOptions"
              item-title="label"
              item-value="value"
              density="compact"
              hide-details
              class="ncrm-onboarding-details__stage-select"
              :disabled="stageMoveLoading || !stageMoveOptions.length"
            />
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              block
              :disabled="!canMoveToSelectedStage || stageMoveLoading"
              :loading="stageMoveLoading"
              @click="moveToSelectedStage"
            >
              {{ $t("ncrm.onboarding.details.actions.moveToStage") }}
            </v-btn>
          </div>

          <div class="text-subtitle-2 ncrm-text-strong mb-2">
            {{ $t("ncrm.onboarding.details.tasks.stagesTitle") }}
          </div>

          <v-alert v-if="!stageItems.length" type="info" density="comfortable" class="mb-3">
            {{ $t("ncrm.onboarding.details.tasks.emptyStages") }}
          </v-alert>

          <div v-else class="ncrm-onboarding-details__tasks-stage-list mb-3">
            <div
              v-for="stage in stageItems"
              :key="`onboarding-stage-right-${stage.id}`"
              class="ncrm-onboarding-details__kv-item"
            >
              <div class="d-flex align-center justify-space-between ga-2">
                <div class="text-body-2 ncrm-text-strong">{{ valueOrDash(stage.name) }}</div>
                <v-chip
                  size="x-small"
                  :color="Number(stage.id) === currentStageId ? 'primary' : 'default'"
                >
                  {{ Number(stage.id) === currentStageId ? $t("ncrm.onboarding.details.tasks.currentStage") : $t("ncrm.onboarding.details.tasks.stage") }}
                </v-chip>
              </div>
                <div class="text-caption text-medium-emphasis mt-2">
                {{
                  $t("ncrm.onboarding.details.tasks.stageProgress", {
                    done: formatNumber(stageWorkDone(stage)),
                    total: formatNumber(stageWorkTotal(stage)),
                  })
                }}
              </div>
              <v-progress-linear
                class="mt-2"
                :model-value="stageProgress(stage)"
                color="primary"
                height="6"
                rounded
              />
            </div>
          </div>

          <div class="text-subtitle-2 ncrm-text-strong mb-2">
            {{ $t("ncrm.onboarding.details.tasks.taskListTitle") }}
          </div>

          <v-alert v-if="!taskItems.length" type="info" density="comfortable" class="mb-3">
            {{ $t("ncrm.onboarding.details.tasks.emptyTasks") }}
          </v-alert>

          <v-list v-else density="compact" class="pa-0 ncrm-onboarding-details__task-list mb-3">
            <v-list-item
              v-for="row in taskItems"
              :key="`onboarding-task-right-${row.id}`"
              class="ncrm-onboarding-details__list-item"
            >
              <v-list-item-title class="text-body-2 ncrm-text-strong">
                {{ valueOrDash(row.title || row.task_template_title) }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ valueOrDash(row.stage_name) }} · {{ formatDateTime(row.due_at) }}
              </v-list-item-subtitle>
              <template #append>
                <v-chip size="x-small" :color="taskStatusColor(row.status)">
                  {{ taskStatusLabel(row.status) }}
                </v-chip>
              </template>
            </v-list-item>
          </v-list>

          <div class="ncrm-onboarding-details__kv-list mb-2">
            <div class="ncrm-onboarding-details__kv-item">
              <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.context.blockedReason") }}</div>
              <div class="text-body-2">{{ valueOrDash(item?.blocked_reason) }}</div>
            </div>
            <div class="ncrm-onboarding-details__kv-item">
              <div class="text-caption text-medium-emphasis">{{ $t("ncrm.onboarding.details.context.closedAt") }}</div>
              <div class="text-body-2">{{ formatDate(item?.closed_at) }}</div>
            </div>
          </div>

          <v-divider class="my-3" />

          <v-btn size="small" variant="text" color="primary" :disabled="!hasDealLink" @click="openDeal">
            <v-icon start>mdi-handshake-outline</v-icon>
            {{ $t("ncrm.onboarding.details.actions.openDeal") }}
          </v-btn>
        </div>
      </v-card>
    </template>
  </NcrmRecordLayout>

  <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3200">
    {{ snackbar.message }}
  </v-snackbar>
</template>

<script>
import axios from "axios";
import NcrmRecordLayout from "@/components/ncrm/shared/NcrmRecordLayout.vue";
import NcrmRecordHeader from "@/components/ncrm/shared/NcrmRecordHeader.vue";
import NcrmActivityTimeline from "@/components/ncrm/shared/NcrmActivityTimeline.vue";
import NcrmContractItemFieldsCard from "@/components/ncrm/contracts/NcrmContractItemFieldsCard.vue";
import NcrmOnboardingOwnerField from "./NcrmOnboardingOwnerField.vue";
import {
  parseNumeric,
  valueOrDash,
  formatNumber,
  formatDate,
  formatDateTime,
  formatMoney,
  statusLabel,
  statusColor,
  healthLabel,
  healthColor,
  taskStatusLabel,
  taskStatusColor,
  headerProgressTickState,
  historyEventLabel,
  historyValueLabel,
  stageWorkTotal,
  stageWorkDone,
  stageProgress,
  resolveNextStageId,
} from "./onboardingDetailsShared.mjs";

// Source anchor kept for unit coverage of the explicit translation pattern:
// ncrm.activity.statuses.${normalized}

export default {
  name: "NcrmOnboardingDetails",
  components: {
    NcrmRecordLayout,
    NcrmRecordHeader,
    NcrmActivityTimeline,
    NcrmContractItemFieldsCard,
    NcrmOnboardingOwnerField,
  },
  props: {
    onboardingId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      stageMoveLoading: false,
      activeTab: "activity",
      item: null,
      stageMoveTargetId: null,
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
    };
  },
  computed: {
    normalizedOnboardingId() {
      const parsed = Number(this.onboardingId || this.$route?.params?.onboardingId || 0);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return Math.trunc(parsed);
    },
    recordTitle() {
      const contractNumber = String(this.item?.contract_number || "").trim();
      const productName = String(this.item?.product_name_snapshot || "").trim();
      if (contractNumber && productName) return `${contractNumber} · ${productName}`;
      if (contractNumber) return contractNumber;
      if (productName) return productName;
      if (this.normalizedOnboardingId) return `#${this.normalizedOnboardingId}`;
      return this.$t("ncrm.onboarding.details.titleFallback");
    },
    recordSubtitle() {
      const templateName = String(this.item?.template_name || "").trim();
      const stageName = String(this.item?.current_stage_name || "").trim();
      if (templateName && stageName) return `${templateName} · ${stageName}`;
      return templateName || stageName || this.$t("ncrm.onboarding.details.subtitleFallback");
    },
    headerChips() {
      return [
        {
          text: this.statusLabel(this.item?.status),
          color: this.statusColor(this.item?.status),
        },
        {
          text: this.healthLabel(this.item?.health),
          color: this.healthColor(this.item?.health),
        },
      ];
    },
    stageItems() {
      return Array.isArray(this.item?.stages) ? this.item.stages : [];
    },
    activeStageItems() {
      return this.stageItems.filter((stage) => Number(stage?.is_active) === 1 || stage?.is_active === true);
    },
    taskItems() {
      return Array.isArray(this.item?.tasks) ? this.item.tasks : [];
    },
    fieldItems() {
      return Array.isArray(this.item?.fields) ? this.item.fields : [];
    },
    historyItems() {
      return Array.isArray(this.item?.history) ? this.item.history : [];
    },
    paymentSummary() {
      return this.item?.payment_summary || {
        total_payments: 0,
        paid_amount: 0,
        open_amount: 0,
        overdue_amount: 0,
        next_due_at: null,
      };
    },
    currentStageId() {
      return parseNumeric(this.item?.current_stage_id, 0);
    },
    visibleFieldStageIds() {
      const stages = Array.isArray(this.stageItems) ? this.stageItems : [];
      const currentStage = stages.find((stage) => parseNumeric(stage?.id, 0) === this.currentStageId);
      const currentSortOrder = parseNumeric(currentStage?.sort_order, null);
      if (!currentStage || currentSortOrder === null) return [];
      return stages
        .filter((stage) => parseNumeric(stage?.sort_order, Number.MAX_SAFE_INTEGER) <= currentSortOrder)
        .map((stage) => parseNumeric(stage?.id, 0))
        .filter((stageId) => stageId > 0);
    },
    headerProgressStages() {
      const sourceStages = this.activeStageItems.length ? this.activeStageItems : this.stageItems;
      return sourceStages
        .map((stage) => {
          const stageId = parseNumeric(stage?.id, 0);
          const stageSortOrder = parseNumeric(stage?.sort_order, 0);
          const totalWork = Math.max(
            parseNumeric(stage?.task_total, 0) + parseNumeric(stage?.required_field_total, 0),
            0
          );
          const tickCount = Math.max(totalWork, 1);
          const doneCount = Math.max(
            0,
            Math.min(
              parseNumeric(stage?.task_done, 0) + parseNumeric(stage?.required_field_completed, 0),
              tickCount
            )
          );
          return {
            id: stageId,
            sortOrder: stageSortOrder,
            tickCount,
            doneCount,
            weight: tickCount,
          };
        })
        .filter((stage) => stage.id > 0)
        .sort((left, right) => {
          if (left.sortOrder !== right.sortOrder) return left.sortOrder - right.sortOrder;
          return left.id - right.id;
        });
    },
    stageMoveOptions() {
      return this.activeStageItems
        .map((stage) => ({
          value: parseNumeric(stage.id, 0),
          sortOrder: parseNumeric(stage.sort_order, 0),
          label: String(stage.name || "").trim() || `#${parseNumeric(stage.id, 0)}`,
        }))
        .filter((stage) => stage.value > 0)
        .sort((left, right) => {
          if (left.sortOrder !== right.sortOrder) return left.sortOrder - right.sortOrder;
          return left.value - right.value;
        });
    },
    canMoveToSelectedStage() {
      const target = parseNumeric(this.stageMoveTargetId, 0);
      return target > 0 && target !== this.currentStageId;
    },
    canMoveToNextStage() {
      return Boolean(this.resolveNextStageId());
    },
    hasContractLink() {
      return parseNumeric(this.item?.contract_id, 0) > 0;
    },
    hasDealLink() {
      return parseNumeric(this.item?.deal_id, 0) > 0;
    },
    contractItemFieldRecord() {
      const contractItemId = parseNumeric(this.item?.contract_item_id, 0);
      if (!contractItemId) return null;
      return {
        id: contractItemId,
        product_name_snapshot: this.item?.product_name_snapshot || "",
        quantity: 1,
        billing_type: "",
        billing_frequency: "",
        total_amount: null,
        currency: "MDL",
        fields: this.fieldItems,
      };
    },
  },
  watch: {
    "$route.params.onboardingId"(nextValue, previousValue) {
      if (String(nextValue || "") === String(previousValue || "")) return;
      this.loadOnboarding();
    },
    activeStageItems: {
      immediate: true,
      handler(nextValue) {
        const options = Array.isArray(nextValue) ? nextValue : [];
        if (!options.length) {
          this.stageMoveTargetId = null;
          return;
        }
        const nextStage = this.resolveNextStageId();
        this.stageMoveTargetId = nextStage || this.currentStageId || parseNumeric(options[0]?.id, null);
      },
    },
  },
  mounted() {
    this.loadOnboarding();
  },
  methods: {
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        color,
        message,
      };
    },
    valueOrDash,
    formatNumber,
    formatDate,
    formatDateTime,
    formatMoney,
    statusLabel,
    statusColor,
    healthLabel,
    healthColor,
    taskStatusLabel,
    taskStatusColor,
    headerProgressTickState,
    historyEventLabel,
    historyValueLabel,
    stageWorkTotal,
    stageWorkDone,
    stageProgress,
    resolveNextStageId,
    goBack() {
      this.$router.push({ name: "NcrmOnboardingHome" });
    },
    openContract() {
      const contractId = parseNumeric(this.item?.contract_id, 0);
      if (!contractId) return;
      this.$router.push({ name: "NcrmContractDetails", params: { contractId: String(contractId) } });
    },
    openDeal() {
      const dealId = parseNumeric(this.item?.deal_id, 0);
      if (!dealId) return;
      this.$router.push({ name: "NcrmDealDetails", params: { id: String(dealId) } });
    },
    async moveStage(stageId = null) {
      if (!this.normalizedOnboardingId) return;
      this.stageMoveLoading = true;
      try {
        const payload = {};
        const normalizedTarget = parseNumeric(stageId, 0);
        if (normalizedTarget) {
          payload.stage_id = normalizedTarget;
        }

        const { data } = await axios.patch(`/ncrm/onboarding/${this.normalizedOnboardingId}/stage`, payload);
        if (data?.item) {
          this.item = data.item;
        }
        const createdTaskCount = parseNumeric(data?.task_generation?.created_task_count, 0);
        this.showSnackbar(
          this.$t("ncrm.onboarding.details.messages.stageMoved", { count: createdTaskCount }),
          "success"
        );
      } catch (error) {
        console.error("[ncrm-onboarding-details] move stage failed", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.onboarding.details.messages.stageMoveError"),
          "error"
        );
      } finally {
        this.stageMoveLoading = false;
      }
    },
    moveToSelectedStage() {
      const target = parseNumeric(this.stageMoveTargetId, 0);
      if (!target || target === this.currentStageId) return;
      this.moveStage(target);
    },
    moveToNextStage() {
      const nextStageId = this.resolveNextStageId();
      if (!nextStageId) return;
      this.moveStage(nextStageId);
    },
    handleFieldNotification(payload = {}) {
      const message = String(payload?.message || "").trim();
      if (!message) return;
      this.showSnackbar(message, payload?.color || "info");
    },
    handleActivityTimelineChanged() {
      this.loadOnboarding();
    },
    async loadOnboarding() {
      if (!this.normalizedOnboardingId) {
        this.item = null;
        return;
      }

      this.loading = true;
      try {
        const { data } = await axios.get(`/ncrm/onboarding/${this.normalizedOnboardingId}`);
        this.item = data || null;
      } catch (error) {
        console.error("[ncrm-onboarding-details] load failed", error);
        this.item = null;
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.onboarding.details.messages.loadError"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-onboarding-details__window {
  min-height: 380px;
}

.ncrm-onboarding-details__header-progress {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  gap: clamp(2px, 0.45vw, 6px);
}

.ncrm-onboarding-details__header-progress-stage {
  --stage-weight: 1;
  flex: var(--stage-weight) 1 0;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  column-gap: clamp(1px, 0.16vw, 2px);
}

.ncrm-onboarding-details__header-progress-stage.is-current-stage {
  box-shadow: 0 0 0 1px rgba(var(--v-theme-warning), 0.55);
  border-radius: 2px;
}

.ncrm-onboarding-details__header-progress-tick {
  display: block;
  height: clamp(5px, 0.6vw, 7px);
  border-radius: 2px;
  background: rgba(var(--v-theme-on-surface), 0.16);
}

.ncrm-onboarding-details__header-progress-tick.is-done {
  background: rgba(var(--v-theme-primary), 0.92);
}

.ncrm-onboarding-details__header-progress-tick.is-current {
  background: rgb(var(--v-theme-warning));
}

.ncrm-onboarding-details__kv-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.ncrm-onboarding-details__kv-item {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 8px;
  padding: 8px 10px;
}

.ncrm-onboarding-details__stage-select {
  min-width: 0;
  width: 100%;
}

.ncrm-onboarding-details__stage-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.ncrm-onboarding-details__tasks-stage-list,
.ncrm-onboarding-details__task-list {
  max-height: 280px;
  overflow: auto;
}

.ncrm-onboarding-details__list-item + .ncrm-onboarding-details__list-item {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}
</style>
